// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Button, Row, Col, View } from "@components";
import type { StepProps } from "@components";
import type {
  AbsenceMonitoring,
  AbsenceMonitoringReason,
} from "@modules/service/reducer/absence-monitoring/types";
import { AbsenceMonitoringActionsForm, CommonReasonInputForm } from "@modules/service/components";

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: Object,
  draft: ?AbsenceMonitoring,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class AbsenceMonitoringActions extends React.Component<Props> {
  validator: *;
  errorMessage: *;

  componentDidMount() {
    if (this.schema) {
      this.validator = new Ajv().compile(this.schema);
    }
  }

  componentDidUpdate() {
    if (!this.validator && this.schema) {
      this.validator = new Ajv().compile(this.schema);
    }
  }

  get data() {
    return this.props.draft?.content;
  }

  get schema() {
    return this.props.schema;
  }

  handleSubmit = () => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(this.data || [], true);
    }
  };

  updateNoActionReasons = (noActionReasons: Array<AbsenceMonitoringReason>) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        noActionReasons,
      },
    });
  };

  updateAdditionalSupportActions = (additionalSupportActions: Array<AbsenceMonitoringReason>) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        additionalSupportActions,
      },
    });
  };

  get isListItemsInvalid() {
    if (!this.data || !this.data.additionalSupportActions || !this.data.noActionReasons) {
      return this.props.t("AT_LEAST_ONE_ACTIVE_SUPPORT_ACTION_MUST_BE_PROVIDED");
    }

    if (
      (this.data.additionalSupportActions || []).length &&
      !this.data.additionalSupportActions.find(el => el.isActive === true && el.action)
    ) {
      return this.props.t("AT_LEAST_ONE_ACTIVE_SUPPORT_ACTION_MUST_BE_PROVIDED");
    }

    if (
      (this.data.noActionReasons || []).length &&
      !this.data.noActionReasons.find(el => el.isActive === true && el.reason)
    ) {
      return this.props.t("AT_LEAST_ONE_ACTIVE_REASON_MUST_BE_PROVIDED");
    }
  }

  render() {
    if (!this.props.editable || !this.schema) {
      return <div />;
    }

    return (
      <View gap={4}>
        <View className="gs-container">
          <Col sm="12">
            {this.data && this.schema.properties.noActionReasons ? (
              <CommonReasonInputForm
                data={this.data.noActionReasons}
                schema={this.schema.properties.noActionReasons.items}
                onChange={this.updateNoActionReasons}
                title={this.props.t("ABSENCE_MONITORING_DETAILS_REASONS_FOR_NO_ACTION")}
                addItemButtonText={this.props.t(
                  "ABSENCE_MONITORING_DETAILS_ADD_REASON_FOR_TAKING_NO_ACTION",
                )}
              />
            ) : null}
          </Col>
        </View>
        <View className="gs-container">
          <Col sm="12">
            {this.data && this.schema.properties.additionalSupportActions ? (
              <AbsenceMonitoringActionsForm
                data={this.data.additionalSupportActions}
                schema={this.schema.properties.additionalSupportActions.items}
                onChange={this.updateAdditionalSupportActions}
                title={this.props.t(
                  "ABSENCE_MONITORING_DETAILS_REASONS_ADDITIONAL_SUPPORT_ACTIONS",
                )}
                addItemButtonText={this.props.t(
                  "ABSENCE_MONITORING_DETAILS_ADD_ADDITIONAL_SUPPORT_ACTION",
                )}
              />
            ) : null}
          </Col>
        </View>
        {!this.isValid && this.errorMessage && (
          <p className={"w-100 d-flex justify-content-center mt-2 mb-2 alert alert-danger"}>
            {this.errorMessage}
          </p>
        )}
        {this.isListItemsValid && (
          <p className={"w-100 d-flex justify-content-center mt-2 mb-2 alert alert-danger"}>
            {this.isListItemsInvalid}
          </p>
        )}

        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button onClick={this.handleSubmit} disabled={this.isListItemsInvalid}>
              {this.props.t("ABSENCE_MONITORING_STEP4_NEXT_BUTTON")}
            </Button>
          </div>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAbsenceMonitoringSchema(state),
    draft: serviceSelectors.getAbsenceMonitoringDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAbsenceMonitoringDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceMonitoringActions);
